import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, Calendar, MapPin, Swords, Users, Sparkles, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function EventVotePage() {
  const nav = useNavigate();
  const [number, setNumber] = useState('');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault?.();
    const n = parseInt(number, 10);
    if (!n || n < 1) { toast.error('Ingresa un número de evento válido'); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/events/by-number/${n}`);
      setEvent(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Evento no encontrado');
      setEvent(null);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-aura-cyan/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-aura-violet/20 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">VOTAR EN EVENTO</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-4">
        <section className="aura-card p-5 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-aura-cyan/20 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.3em] text-aura-textDim">Paso 1</div>
            <h1 className="font-display font-black text-white text-2xl mt-1">Identifica el evento</h1>
            <p className="text-xs text-aura-textDim mt-1">Ingresa el número que aparece en el póster o pantalla.</p>

            <form onSubmit={lookup} className="mt-5 flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-aura-cyan font-black text-sm">#EVT-</span>
                <input
                  data-testid="in-event-number"
                  type="number" min="1" step="1" required inputMode="numeric"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="0001"
                  className="w-full bg-aura-surface2 border border-white/10 rounded-xl pl-16 pr-3 py-3 text-lg text-white font-mono font-black outline-none focus:border-aura-cyan/60"
                />
              </div>
              <button data-testid="btn-lookup" type="submit" disabled={loading} className="aura-btn-primary !py-3 !px-4">
                <Search size={16} />
              </button>
            </form>
          </div>
        </section>

        {loading && <div className="text-center py-10 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>}

        {event && !loading && (
          <>
            <section className="aura-card p-5" data-testid="event-summary">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono text-aura-cyan tracking-[0.25em]">#EVT-{String(event.event_number).padStart(4, '0')}</div>
                <div className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-display font-bold ${
                  event.status === 'live' ? 'bg-aura-pink/20 text-aura-pink' :
                  event.status === 'scheduled' ? 'bg-aura-cyan/20 text-aura-cyan' :
                  event.status === 'finished' ? 'bg-white/10 text-white/70' :
                  'bg-red-500/20 text-red-400'
                }`}>{event.status}</div>
              </div>
              <h2 className="mt-2 font-display font-black text-white text-xl">{event.title}</h2>
              <div className="mt-2 space-y-1 text-xs text-aura-textDim">
                <div className="flex items-center gap-2"><Calendar size={12} className="text-aura-cyan" /> {fmtDate(event.event_datetime)}</div>
                <div className="flex items-center gap-2"><MapPin size={12} className="text-aura-cyan" /> {event.venue_name ? `${event.venue_name} · ` : ''}{event.city}</div>
                {event.organizer?.username && (
                  <div className="flex items-center gap-2">
                    <Sparkles size={12} className="text-aura-gold" />
                    Organiza <Link to={`/u/${event.organizer.username}`} className="text-white font-semibold hover:text-aura-cyan">@{event.organizer.username}</Link>
                  </div>
                )}
              </div>
            </section>

            {/* Winners */}
            {event.winners?.length > 0 && (
              <section className="aura-card p-4" data-testid="event-winners">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={16} className="text-aura-gold" />
                  <div className="font-display font-black text-aura-gold tracking-widest text-sm">GANADORES</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {event.winners.map((w) => (
                    <div key={w.user_id} className="flex items-center gap-2 bg-aura-gold/10 border border-aura-gold/30 rounded-full pl-1 pr-3 py-1">
                      <UserAvatar user={w} size="xs" />
                      <span className="text-white font-display font-semibold text-xs">@{w.username}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Contestants roster */}
            <section className="aura-card p-4" data-testid="event-contestants">
              <div className="flex items-center gap-2 mb-3">
                <Users size={14} className="text-aura-cyan" />
                <div className="font-display font-black text-white tracking-widest text-sm">
                  CONTENDIENTES REGISTRADOS ({event.contestants_count})
                </div>
              </div>
              {event.contestants?.length === 0 ? (
                <div className="text-center text-xs text-aura-textDim py-4">Aún no hay contendientes registrados.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {event.contestants.map((c) => (
                    <Link key={c.user_id} to={`/u/${c.user?.username}`} className="flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-xl p-2 hover:border-aura-cyan/40 transition">
                      <UserAvatar user={c.user} size="xs" />
                      <div className="min-w-0">
                        <div className="text-xs font-display font-semibold text-white truncate">{c.user?.name}</div>
                        <div className="text-[10px] text-aura-textDim truncate">@{c.user?.username}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Battles */}
            <section className="aura-card p-4" data-testid="event-battles">
              <div className="flex items-center gap-2 mb-3">
                <Swords size={14} className="text-aura-pink" />
                <div className="font-display font-black text-white tracking-widest text-sm">
                  BATALLAS DEL EVENTO ({event.battles_count})
                </div>
              </div>
              {event.battles?.length === 0 ? (
                <div className="text-center text-xs text-aura-textDim py-4">Sin batallas todavía. Espera a que el organizador cree la primera.</div>
              ) : (
                <ul className="space-y-3" data-testid="battles-list">
                  {event.battles.map((b) => (
                    <li key={b.battle_id}>
                      <Link
                        to={`/battle/${b.battle_id}`}
                        data-testid={`battle-${b.battle_number || b.battle_id.slice(-6)}`}
                        className="block rounded-xl border border-white/10 bg-white/[0.02] p-3 hover:border-aura-cyan/40 transition"
                      >
                        <div className="flex items-center justify-between text-[10px] font-mono">
                          <span className="text-aura-cyan tracking-[0.25em]">
                            {b.battle_number ? `#B-${String(b.battle_number).padStart(3, '0')}` : '#B'}
                            <span className="ml-2 text-white/60">· {b.format || '1v1'}</span>
                          </span>
                          <span className={`px-2 py-0.5 rounded-full uppercase font-display font-bold ${
                            b.status === 'voting' ? 'bg-aura-pink/20 text-aura-pink animate-aura-pulse' :
                            b.status === 'active' ? 'bg-aura-cyan/20 text-aura-cyan' :
                            b.status === 'preparing' ? 'bg-white/10 text-white/70' :
                            b.status === 'completed' || b.status === 'tied' ? 'bg-white/10 text-white/70' :
                            'bg-red-500/20 text-red-400'
                          }`}>{b.status === 'voting' ? '⚡ VOTA AHORA' : b.status}</span>
                        </div>

                        <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                          <TeamStack team={b.team_a} side="a" winning={b.winning_side === 'a'} />
                          <div className="text-center">
                            <div className="font-display font-black text-xl aura-text-gradient">VS</div>
                          </div>
                          <TeamStack team={b.team_b} side="b" winning={b.winning_side === 'b'} align="right" />
                        </div>

                        {b.total_votes > 0 && (
                          <div className="mt-2 text-[10px] text-aura-textDim text-center font-mono">{b.total_votes} votos</div>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

function TeamStack({ team = [], side, winning = false, align = 'left' }) {
  const list = team.slice(0, 3);
  const extra = team.length - list.length;
  return (
    <div className={`flex ${align === 'right' ? 'flex-row-reverse' : ''} items-center gap-2 min-w-0`}>
      <div className="flex -space-x-2 shrink-0">
        {list.map((u) => (
          <div key={u.user_id} className={`rounded-full ring-2 ${winning ? 'ring-aura-gold' : 'ring-aura-bg'}`}>
            <UserAvatar user={u} size="sm" />
          </div>
        ))}
        {extra > 0 && (
          <div className="h-8 w-8 rounded-full bg-aura-surface2 border border-white/10 grid place-items-center text-[10px] text-white/70 font-mono">+{extra}</div>
        )}
      </div>
      <div className={`min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
        <div className={`text-[9px] uppercase tracking-widest font-display font-black ${winning ? 'text-aura-gold' : side === 'a' ? 'text-aura-cyan' : 'text-aura-pink'}`}>
          EQUIPO {side === 'a' ? 'A' : 'B'} {winning && '· 🏆'}
        </div>
        <div className="text-xs font-display font-bold text-white truncate">
          {list.map((u) => `@${u.username}`).join(', ')}
        </div>
      </div>
    </div>
  );
}
